import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { brandKeys } from '@/shared/api/brandQueries'
import { fetchMeProfile, meQueryKey, meQueryOptions } from '@/shared/api/meQueries'
import { clearAuthenticatedClientSession } from '@/shared/lib/authSessionCleanup'
import { ensureCsrfCookie, httpClient } from '../../../shared/lib/httpClient'
import { apiV1 } from '@/shared/api/apiPrefix'
import type {
  ForgotPasswordInput,
  LoginInput,
  ResetPasswordInput,
  TwoFactorLoginInput,
} from '../schemas/auth.schema'
import type { AcceptInvitationInput, RegisterInput } from '../schemas/registration.schema'
import { registerPushSubscription } from '@/shared/platform/notifications.web'

type LoginUser = {
  id: string
  name: string
  email: string
  last_login_at: string | null
}

type LoginSuccess = {
  kind: 'authenticated'
  user: LoginUser
}

type LoginTwoFactorChallenge = {
  kind: 'two_factor'
  challenge_token: string
  expires_in: number
}

export type LoginOutcome = LoginSuccess | LoginTwoFactorChallenge

function applyAuthenticatedSession(
  qc: QueryClient,
  user: LoginUser,
): Promise<void> {
  void qc.invalidateQueries({ queryKey: brandKeys.authenticatedSnapshot() })
  void registerPushSubscription()
  Sentry.setUser({ id: String(user.id), email: user.email })

  return prefetchAuthenticatedProfile(qc)
}

async function prefetchAuthenticatedProfile(qc: QueryClient): Promise<void> {
  try {
    const profile = await qc.fetchQuery({
      ...meQueryOptions,
      queryFn: () => fetchMeProfile({ redirectOnUnauthenticated: false }),
    })
    qc.setQueryData(meQueryKey, profile)
  } catch (error) {
    Sentry.captureException(error, { tags: { area: 'auth_profile_prefetch' } })
    throw error
  }
}

export function useLoginMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: LoginInput): Promise<LoginOutcome> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{
        data:
          | { user: LoginUser }
          | { two_factor_required: true; challenge_token: string; expires_in: number }
      }>(apiV1('/auth/login'), input)
      const data = res.data.data
      if ('two_factor_required' in data && data.two_factor_required) {
        return {
          kind: 'two_factor',
          challenge_token: data.challenge_token,
          expires_in: data.expires_in,
        }
      }
      if (!('user' in data)) {
        throw new Error('Unexpected login response')
      }
      return { kind: 'authenticated', user: data.user }
    },
    onSuccess: async (outcome) => {
      if (outcome.kind === 'authenticated') {
        await applyAuthenticatedSession(qc, outcome.user)
      }
    },
  })
}

export function useLoginTwoFactorMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: TwoFactorLoginInput): Promise<LoginUser> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { user: LoginUser } }>(
        apiV1('/auth/login/two-factor'),
        input,
      )
      return res.data.data.user
    },
    onSuccess: async (user) => {
      await applyAuthenticatedSession(qc, user)
    },
  })
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: async (input: ForgotPasswordInput): Promise<{ sent: boolean }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { sent: boolean } }>(
        apiV1('/auth/forgot-password'),
        input,
      )
      return res.data.data
    },
  })
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: async (input: ResetPasswordInput): Promise<{ reset: boolean }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { reset: boolean } }>(
        apiV1('/auth/reset-password'),
        input,
      )
      return res.data.data
    },
  })
}

// SEC-A5: the backend always answers "pending" now, whatever the submitted email actually maps
// to (new/already-active/ineligible/eligible-for-self-activation) - never a distinguishing value.
type RegistrationStatus = 'pending'

export function useRegisterMutation() {
  return useMutation({
    mutationFn: async (input: RegisterInput): Promise<{ status: RegistrationStatus }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { status: RegistrationStatus } }>(
        apiV1('/auth/register'),
        {
          email: input.email,
          password: input.password,
          password_confirmation: input.password_confirmation,
        },
      )
      return res.data.data
    },
  })
}

export function useConfirmRegistrationMutation() {
  return useMutation({
    mutationFn: async (token: string): Promise<{ activated: boolean }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { activated: boolean } }>(
        apiV1('/auth/register/confirm'),
        { token },
      )
      return res.data.data
    },
  })
}

export function useAcceptInvitationMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: AcceptInvitationInput): Promise<{ activated: boolean }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { activated: boolean } }>(
        apiV1('/auth/accept-invitation'),
        {
          token: input.token,
          password: input.password,
          password_confirmation: input.password_confirmation,
        },
      )
      return res.data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

export function useRequestMagicLinkMutation() {
  return useMutation({
    mutationFn: async (email: string): Promise<{ message: string }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { message: string } }>(apiV1('/auth/magic-link'), { email })
      return res.data.data
    },
  })
}

export function useConsumeMagicLinkMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (token: string): Promise<{ status: string }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { status: string } }>(apiV1('/auth/magic-link/consume'), { token })
      return res.data.data
    },
    onSuccess: async () => {
      await prefetchAuthenticatedProfile(qc)
      void qc.invalidateQueries({ queryKey: brandKeys.authenticatedSnapshot() })
    },
  })
}

export function useExchangeImpersonationMutation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (token: string): Promise<{ status: string }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { status: string } }>(
        apiV1('/auth/impersonation/exchange'),
        { token },
      )
      return res.data.data
    },
    onSuccess: async () => {
      await prefetchAuthenticatedProfile(qc)
      void qc.invalidateQueries({ queryKey: brandKeys.authenticatedSnapshot() })
    },
  })
}

export function useLeaveImpersonationMutation() {
  return useMutation({
    mutationFn: async (): Promise<{ admin_url: string }> => {
      await ensureCsrfCookie()
      const res = await httpClient.post<{ data: { admin_url: string } }>(apiV1('/auth/impersonation/leave'))
      return res.data.data
    },
    onSuccess: () => {
      clearAuthenticatedClientSession()
    },
  })
}

export function useLogoutMutation() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await ensureCsrfCookie()
      await httpClient.post<null>(apiV1('/auth/logout'))
    },
    onSuccess: () => {
      clearAuthenticatedClientSession()
    },
  })
}
